import { computed, inject } from '@angular/core'
import { Component } from '@angular/core'
import { DashboardService } from 'src/app/shared/services/dashboard.service'
import { DashboardConfig } from 'src/app/shared/models/dashboardConfig'
import { environment } from 'src/environments/environment'
import { MatToolbar } from '@angular/material/toolbar'
import { MatIconButton } from '@angular/material/button'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatIcon } from '@angular/material/icon'
import { NavGroupComponent } from './nav-group.component'
import { NavLinkDirective } from './nav-link.directive'
import { navGroups } from '../routes'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

const buildVersion = environment.buildVersion === '<ESS_BUILD_VERSION>' ? '(local)' : environment.buildVersion

// noinspection AngularNgOptimizedImage
@Component({
  selector: 'nav-bar',
  imports: [
    MatToolbar,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    NavGroupComponent,
    MatMenuItem,
    NavLinkDirective,
  ],
  template: `
    <mat-toolbar color="primary" class="mat-elevation-z5">
      <button class="nav-btn" mat-icon-button [matMenuTriggerFor]="menu">
        <img
            class="ameren-logo"
            src="../../assets/images/ameren-logo-dark.svg"
            alt="Ameren Logo"
        />
        <mat-icon svgIcon="expand_more"></mat-icon>
      </button>

      <mat-menu #menu="matMenu" [overlapTrigger]="false">
        <a mat-menu-item navLink="/">Home</a>

        <button mat-menu-item [matMenuTriggerFor]="settlementProcessMenu">
          <span>{{ navGroups.settlement.title }}</span>
        </button>

        <button mat-menu-item [matMenuTriggerFor]="dataExplorationMenu">
          <span>{{ navGroups.dataExploration.title }}</span>
        </button>

        <button mat-menu-item [matMenuTriggerFor]="dataManagementMenu">
          <span>{{ navGroups.dataManagement.title }}</span>
        </button>

        <button mat-menu-item [matMenuTriggerFor]="systemConfigurationMenu">
          <span>{{ navGroups.systemConfig.title }}</span>
        </button>

        @for (dashboard of rootDashboards(); track dashboard.dashboard_id) {
          <a mat-menu-item [navLink]="'/' + dashboard.name">
            <span>{{ dashboard.display }}</span>
          </a>
        }
      </mat-menu>

      <mat-menu #settlementProcessMenu="matMenu">
        <app-nav-group [group]="navGroups.settlement" />
      </mat-menu>

      <mat-menu #dataExplorationMenu="matMenu">
        <app-nav-group [group]="navGroups.dataExploration" />
      </mat-menu>

      <mat-menu #dataManagementMenu="matMenu">
        <app-nav-group [group]="navGroups.dataManagement" />
      </mat-menu>

      <mat-menu #systemConfigurationMenu="matMenu">
        <app-nav-group [group]="navGroups.systemConfig" />
      </mat-menu>
    </mat-toolbar>
    <h1 class="app-name">Electric Settlement System</h1>
    <span class="build-version">{{ versionText }}</span>
  `,
  styles: `
    :host {
      height: 4rem;
      position: relative;
      display: grid;
      grid-template-columns: 7rem 1fr;
      text-align: center;
      align-items: center;
      background-color: #218737;
      color: white;
      
      mat-toolbar {
        padding: 0;
        box-shadow: none;
        height: 100%;
        background-color: #218737;
        
        button.nav-btn {
          padding: 0;
          display: grid;
          height: 100%;
          width: 100%;
          grid-template-columns: 1fr 1.5rem;
          align-items: center;
          justify-items: end;
          cursor: pointer;

          .ameren-logo {
            height: 4rem;
            width: 71px;
          }

          mat-icon {
            margin-top: 1rem;
          }
        }
      }

      .app-name {
        margin: 1rem 0 0 -7rem;
        color: white;
      }

      .build-version {
        position: absolute;
        font-size: 14px;
        right: 16px;
        bottom: 12px;
        text-align: start;
      }
    }
  `,
})
export class NavBarComponent {
  private readonly dashboardService = inject(DashboardService)

  constructor() {
    // make sure the dashboards get loaded dynamically
    // takeUntilDestroyed not really necessary since the navbar presumably will live for the lifetime of the session
    // but just in case that changes ...
    this.dashboardService.get().pipe(takeUntilDestroyed()).subscribe()
  }

  protected rootDashboards = computed((): readonly DashboardConfig[] =>
    this.dashboardService.dashboardsSignal().filter(d => !d.hidden && !d.nav_group),
  )

  protected readonly navGroups = navGroups
  protected readonly versionText = `${environment.environmentName} ${buildVersion}`
}
